import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import FeedbackModal from './FeedbackModal.jsx';
import {
  ANCHORS,
  DEFAULT_ANCHOR,
  anchorToPoint,
  clampPoint,
  clearAnchor,
  loadAnchor,
  nearestAnchor,
  saveAnchor,
} from './feedbackWidgetPosition.js';

const DRAG_THRESHOLD = 8; // px of movement before a press counts as a drag

function getViewport() {
  return { width: window.innerWidth, height: window.innerHeight };
}

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(DEFAULT_ANCHOR);
  const [dragging, setDragging] = useState(false);
  // Live pixel position while dragging; null means "follow the anchor".
  const [dragPoint, setDragPoint] = useState(null);

  const btnRef = useRef(null);
  // Mutable drag bookkeeping kept off React state to avoid re-render churn.
  const drag = useRef({ active: false, moved: false, startX: 0, startY: 0, grabX: 0, grabY: 0, box: null });

  // Restore persisted anchor on mount.
  useEffect(() => {
    setAnchor(loadAnchor());
  }, []);

  // Resolve current anchor to a pixel point so the button never sits
  // off-screen (also covers the resize case below).
  const resolvePoint = useCallback(() => {
    const el = btnRef.current;
    if (!el) return null;
    const box = { width: el.offsetWidth, height: el.offsetHeight };
    return anchorToPoint(anchor, getViewport(), box);
  }, [anchor]);

  // Re-clamp / re-resolve on resize so the button stays on-screen.
  useLayoutEffect(() => {
    function onResize() {
      // Forcing a re-render via anchor identity keeps the style fresh.
      setAnchor((a) => a);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = useCallback((e) => {
    // Only primary button / touch / pen.
    if (e.button !== undefined && e.button !== 0) return;
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    drag.current = {
      active: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      grabX: e.clientX - rect.left,
      grabY: e.clientY - rect.top,
      box: { width: rect.width, height: rect.height },
    };
    try { el.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
  }, []);

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    d.moved = true;
    if (!dragging) setDragging(true);
    const raw = { x: e.clientX - d.grabX, y: e.clientY - d.grabY };
    setDragPoint(clampPoint(raw, getViewport(), d.box));
  }, [dragging]);

  const endDrag = useCallback((e) => {
    const d = drag.current;
    if (!d.active) return;
    d.active = false;
    const el = btnRef.current;
    try { el && el.releasePointerCapture(e.pointerId); } catch { /* ignore */ }

    if (d.moved && dragPoint) {
      const snapped = nearestAnchor(dragPoint, getViewport(), d.box);
      setAnchor(snapped);
      saveAnchor(snapped);
    }
    setDragging(false);
    setDragPoint(null);
  }, [dragPoint]);

  const onClick = useCallback(() => {
    // Suppress the click that fires at the end of a real drag.
    if (drag.current.moved) {
      drag.current.moved = false;
      return;
    }
    setOpen(true);
  }, []);

  const resetPosition = useCallback(() => {
    clearAnchor();
    setAnchor(DEFAULT_ANCHOR);
  }, []);

  const point = dragPoint || resolvePoint();
  const positionStyle = point
    ? { left: `${point.x}px`, top: `${point.y}px`, right: 'auto', bottom: 'auto' }
    : {};

  return (
    <>
      {dragging ? <AnchorGhosts btnRef={btnRef} /> : null}
      <button
        ref={btnRef}
        type="button"
        aria-label="Send feedback"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClick={onClick}
        // z-40 instead of z-50 so open modals (z-50/60/100) reliably appear
        // above the widget. (audit-2026-06-05 P3-2 / UI-5)
        className="fixed z-40 bg-[#7C3AED] hover:bg-civix-purpleDeep text-white rounded-full px-4 py-3 shadow-lg flex items-center gap-2 text-sm font-medium select-none"
        style={{
          ...positionStyle,
          touchAction: 'none',
          cursor: dragging ? 'grabbing' : 'grab',
          opacity: dragging ? 0.85 : 1,
          transition: dragging ? 'none' : 'left 200ms ease, top 200ms ease',
          boxShadow: dragging
            ? '0 16px 40px rgba(124,58,237,0.65)'
            : '0 8px 24px rgba(124,58,237,0.45)',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Feedback
      </button>
      {open ? <FeedbackModal onClose={() => setOpen(false)} onResetPosition={resetPosition} /> : null}
    </>
  );
}

// Subtle dashed circles showing where the button will snap.
function AnchorGhosts({ btnRef }) {
  const el = btnRef.current;
  const box = el
    ? { width: el.offsetWidth, height: el.offsetHeight }
    : { width: 120, height: 44 };
  const viewport = getViewport();
  return (
    <div aria-hidden="true" className="fixed inset-0 z-30 pointer-events-none">
      {ANCHORS.map((a) => {
        const p = anchorToPoint(a, viewport, box);
        return (
          <div
            key={a}
            className="absolute rounded-full"
            style={{
              left: `${p.x}px`,
              top: `${p.y}px`,
              width: `${box.width}px`,
              height: `${box.height}px`,
              border: '2px dashed rgba(124,58,237,0.55)',
              background: 'rgba(124,58,237,0.08)',
            }}
          />
        );
      })}
    </div>
  );
}
